
import React, { useState, useRef } from 'react';
import { Card, Button, Input } from '../components/UI';
import { useLeague } from '../components/LeagueContext';
import { Team } from '../types';
import { Edit2, Plus, Trash2, Upload, X, Car } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { PageHero } from '../components/PageHero';

interface ExtendedTeam extends Team {
    carImageUrl?: string;
}

export const TeamsAdmin: React.FC = () => {
    const { teams, updateTeam, addTeam, deleteTeam } = useLeague();
    const [editingTeam, setEditingTeam] = useState<Partial<ExtendedTeam> | null>(null);
    const [isNew, setIsNew] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const carInputRef = useRef<HTMLInputElement>(null);

    const handleEdit = (team: Team) => {
        setEditingTeam({ ...team });
        setIsNew(false);
    };

    const handleCreate = () => {
        setEditingTeam({ name: '', color: '#ffffff', logoUrl: '', carImageUrl: '' });
        setIsNew(true);
    };

    const handleSave = () => {
        if (!editingTeam || !editingTeam.name) return;

        if (isNew) {
            const newTeam: any = {
                id: `t${Date.now()}`,
                name: editingTeam.name,
                color: editingTeam.color || '#ffffff',
                logoUrl: editingTeam.logoUrl,
                carImageUrl: editingTeam.carImageUrl
            };
            addTeam(newTeam);
        } else if (editingTeam.id) {
            updateTeam(editingTeam as Team);
        }
        setEditingTeam(null);
    };

    const handleUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'logoUrl' | 'carImageUrl') => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditingTeam(prev => prev ? ({ ...prev, [field]: reader.result as string }) : null);
            };
            reader.readAsDataURL(file);
        }
    };

    const getImageUrl = (url?: string) => {
        if (!url) return undefined;
        let finalUrl = url;
        if (!url.startsWith('http') && !url.startsWith('data:')) {
            const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
            finalUrl = baseUrl ? `${baseUrl}/${url}` : url;
        }
        // CACHE BUSTER
        if (finalUrl && !finalUrl.startsWith('data:')) {
             finalUrl += (finalUrl.includes('?') ? '&' : '?') + 't=' + new Date().getTime();
        }
        return finalUrl;
    };

    return (
        <div className="mx-auto max-w-7xl space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Team Registry"
                title="Teamverwaltung"
                description="Teams, Farben, Logos und Fahrzeug-Lackierungen für Fahrerfeld, Tabellen und Grafiken pflegen."
                icon={Car}
                accent="#22c55e"
                stats={[{ label: 'Teams', value: teams.length }, { label: 'Mit Logo', value: teams.filter(team => team.logoUrl).length }, { label: 'Mit Fahrzeug', value: teams.filter(team => (team as any).carImageUrl).length }]}
                actions={<Button icon={Plus} onClick={handleCreate}>Team anlegen</Button>}
            />

            {/* Edit Modal / Panel */}
            {editingTeam && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-lg bg-f1-dark border-gray-700 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6 border-b border-gray-800 pb-2">
                    <h2 className="text-xl font-bold uppercase">{isNew ? 'Team anlegen' : 'Team bearbeiten'}</h2>
                            <button onClick={() => setEditingTeam(null)} className="text-gray-400 hover:text-white"><X /></button>
                        </div>
                        
                        <div className="space-y-4">
                            <Input 
                                label="Teamname" 
                                value={editingTeam.name} 
                                onChange={e => setEditingTeam({...editingTeam, name: e.target.value})}
                            />
                            
                            <div>
                                <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Teamfarbe</label>
                                <div className="flex gap-4 items-center">
                                    <input 
                                        type="color" 
                                        value={editingTeam.color} 
                                        onChange={e => setEditingTeam({...editingTeam, color: e.target.value})}
                                        className="h-10 w-20 rounded cursor-pointer bg-transparent"
                                    />
                                    <span className="font-mono text-sm text-gray-400">{editingTeam.color}</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Team Logo</label>
                                    <div className="w-full h-24 bg-gray-800 rounded flex items-center justify-center border border-gray-700 overflow-hidden relative group">
                                        {editingTeam.logoUrl ? (
                                            <img src={editingTeam.logoUrl.startsWith('data:') ? editingTeam.logoUrl : getImageUrl(editingTeam.logoUrl)} alt="Preview" className="max-w-full max-h-full object-contain p-2" />
                                        ) : (
                                            <span className="text-xs text-gray-500">Kein Logo</span>
                                        )}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                                            <Upload className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'logoUrl')} />
                                    <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()} className="w-full mt-2">Logo hochladen</Button>
                                </div>

                                <div>
                                    <label className="block text-gray-400 text-sm font-bold mb-2 uppercase">Fahrzeug-Lackierung</label>
                                    <div className="w-full h-24 bg-gray-800 rounded flex items-center justify-center border border-gray-700 overflow-hidden relative group">
                                        {editingTeam.carImageUrl ? (
                                            <img src={editingTeam.carImageUrl.startsWith('data:') ? editingTeam.carImageUrl : getImageUrl(editingTeam.carImageUrl)} alt="Car" className="max-w-full max-h-full object-contain p-1" />
                                        ) : (
                                            <Car className="text-gray-600"/>
                                        )}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer" onClick={() => carInputRef.current?.click()}>
                                            <Upload className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <input type="file" ref={carInputRef} className="hidden" accept="image/*" onChange={(e) => handleUpload(e, 'carImageUrl')} />
                                    <Button size="sm" variant="secondary" onClick={() => carInputRef.current?.click()} className="w-full mt-2">Fahrzeug hochladen</Button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 pt-4 border-t border-gray-800 flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => setEditingTeam(null)}>Abbrechen</Button>
                            <Button onClick={handleSave}>Team speichern</Button>
                        </div>
                    </Card>
                </div>
            )}

            {/* Team Grid with High-Fidelity Cards (Identical to GridOverview) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {teams.map(team => (
                    <div key={team.id} className="group relative flex h-64 flex-col overflow-hidden rounded-[1.75rem] border border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_22px_60px_rgba(0,0,0,0.22)] transition-all duration-300 hover:-translate-y-1 hover:border-white/[0.14]">
                        {/* Edit Controls Overlay */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 z-50">
                            <button onClick={() => handleEdit(team)} className="p-2 bg-gray-900 rounded-full hover:bg-gray-700 text-white border border-gray-600 shadow-lg"><Edit2 size={14} /></button>
                            <button onClick={() => { if(confirm('Delete team?')) deleteTeam(team.id); }} className="p-2 bg-red-900/80 rounded-full hover:bg-red-800 text-white border border-red-700 shadow-lg"><Trash2 size={14} /></button>
                        </div>

                        {/* Top Color Bar */}
                        <div className="h-1.5 w-full" style={{ backgroundColor: team.color }}></div>

                        {/* Content */}
                        <div className="flex-1 flex flex-col items-center justify-center relative p-4 space-y-2">
                             {/* Glow - Stronger */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-30 blur-[50px] transition-opacity duration-300 group-hover:opacity-50" style={{backgroundColor: team.color}}></div>

                            {/* Car - Above Logo, Smaller, Straight */}
                            <div className="relative z-10 h-20 flex items-center justify-center w-full">
                                {(team as any).carImageUrl ? (
                                    <img 
                                        src={getImageUrl((team as any).carImageUrl)} 
                                        className="max-h-full max-w-full object-contain drop-shadow-[0_5px_15px_rgba(0,0,0,0.5)]" 
                                    />
                                ) : (
                                    <Car className="text-gray-700 opacity-50 w-12 h-12" />
                                )}
                            </div>

                            {/* Logo */}
                            <div className="w-16 h-16 rounded-full flex items-center justify-center shadow-xl relative z-10 bg-[#1a1a20] border-2" style={{ borderColor: team.color }}>
                                {team.logoUrl ? (
                                    <img src={getImageUrl(team.logoUrl)} className="w-10 h-10 object-contain" />
                                ) : (
                                    <span className="text-xs font-bold text-gray-600">{team.name.substring(0,2)}</span>
                                )}
                            </div>

                            <div className="text-center z-10">
                                <h3 className="text-lg font-black uppercase text-white tracking-wider leading-tight drop-shadow-md">
                                    {team.name}
                                </h3>
                                <div className="text-[10px] font-mono text-gray-500 mt-1">{team.color}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
